import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    useTheme,
    alpha,
    TextField,
    MenuItem,
    IconButton,
    Collapse,
    InputAdornment,
    Chip
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DataContext } from '../../context/DataContext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const ViewGirvi = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { girvi, loading, error, girviPayments } = useContext(DataContext);
    
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [amountRange, setAmountRange] = useState({ min: '', max: '' });
    const [dateRange, setDateRange] = useState({
        start: null,
        end: null
    });
    const [showFilters, setShowFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState([]);

    // Calculate stats from filtered data
    const filteredGirvi = useMemo(() => {
        if (!girvi) return [];

        return girvi.filter(item => {
            // Search by customer name
            if (searchTerm && !item.customerName.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Status filter
            if (statusFilter !== 'all') {
                // You might need to add a status field to your girvi objects
                // For now, we'll assume all are active
                if (statusFilter === 'active' && !item.isActive) return false;
                if (statusFilter === 'completed' && item.isActive) return false;
            }

            // Amount range filter
            const principal = parseFloat(item.principal) || 0;
            if (amountRange.min && principal < parseFloat(amountRange.min)) return false;
            if (amountRange.max && principal > parseFloat(amountRange.max)) return false;

            // Date range filter (assuming you have a date field)
            if (dateRange.start && item.date) {
                const itemDate = new Date(item.date);
                if (dateRange.start > itemDate) return false;
            }
            if (dateRange.end && item.date) {
                const itemDate = new Date(item.date);
                if (dateRange.end < itemDate) return false;
            }

            return true;
        });
    }, [girvi, searchTerm, statusFilter, amountRange, dateRange]);

    // Update active filters display
    useMemo(() => {
        const filters = [];
        if (searchTerm) filters.push(`Customer: "${searchTerm}"`);
        if (statusFilter !== 'all') filters.push(`Status: ${statusFilter}`);
        if (amountRange.min || amountRange.max) {
            const range = [];
            if (amountRange.min) range.push(`Min: ₹${amountRange.min}`);
            if (amountRange.max) range.push(`Max: ₹${amountRange.max}`);
            filters.push(`Amount: ${range.join(' - ')}`);
        }
        if (dateRange.start || dateRange.end) {
            const range = [];
            if (dateRange.start) range.push(`From: ${dateRange.start.toLocaleDateString()}`);
            if (dateRange.end) range.push(`To: ${dateRange.end.toLocaleDateString()}`);
            filters.push(`Date: ${range.join(' to ')}`);
        }
        setActiveFilters(filters);
    }, [searchTerm, statusFilter, amountRange, dateRange]);

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setAmountRange({ min: '', max: '' });
        setDateRange({ start: null, end: null });
    };

    // Remove specific filter
    const removeFilter = (index) => {
        const filterText = activeFilters[index];
        if (filterText.includes('Customer:')) setSearchTerm('');
        else if (filterText.includes('Status:')) setStatusFilter('all');
        else if (filterText.includes('Amount:')) setAmountRange({ min: '', max: '' });
        else if (filterText.includes('Date:')) setDateRange({ start: null, end: null });
    };

    const stats = useMemo(() => {
        if (!filteredGirvi || !girviPayments) return {
            totalPrincipal: 0,
            totalPending: 0,
            totalReceived: 0,
            totalInterest: 0
        };

        const initialStats = filteredGirvi.reduce((acc, curr) => {
            acc.totalPrincipal += parseFloat(curr.initialPrincipal) || 0;
            acc.totalPending += parseFloat(curr.totalPayable) || 0;
            return acc;
        }, {
            totalPrincipal: 0,
            totalPending: 0
        });

        const totalReceived = girviPayments.reduce((acc, curr) => {
            return acc + (parseFloat(curr.paymentAmount) || 0);
        }, 0);

        return {
            ...initialStats,
            totalReceived,
            totalInterest: totalReceived + initialStats.totalPending - initialStats.totalPrincipal
        };
    }, [filteredGirvi, girviPayments]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    if (error) {
        return (
            <Box sx={{ 
                minHeight: '100vh',
                background: isDark 
                    ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
                    : "radial-gradient( circle 476px at 54.8% 51.5%, rgba(168,229,253,1) 0%, rgba(244,244,254,1) 42.3%, rgba(244,244,254,1) 100.2% )",
                py: 4,
                px: { xs: 2, sm: 3, md: 4 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Alert severity="error" sx={{ 
                    maxWidth: '600px',
                    borderRadius: '16px',
                    backgroundColor: isDark 
                        ? 'rgba(30, 41, 59, 0.7)'
                        : 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    color: theme.palette.error.main,
                    '& .MuiAlert-icon': {
                        color: theme.palette.error.main
                    }
                }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh',
            py: 4,
            px: { xs: 2, sm: 3, md: 4 }
        }}>
            <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                {/* Main Dashboard Card */}
                <Paper 
                    elevation={0}
                    sx={{
                        p: { xs: 3, sm: 4 },
                        mb: 4,
                        backgroundColor: isDark 
                            ? 'rgba(30, 41, 59, 0.4)'
                            : 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: `1px solid ${isDark 
                            ? 'rgba(148, 163, 184, 0.1)'
                            : 'rgba(255, 255, 255, 0.2)'
                        }`,
                        borderRadius: '24px',
                        boxShadow: isDark 
                            ? '0 8px 32px rgba(0, 0, 0, 0.2)'
                            : '0 8px 32px rgba(31, 38, 135, 0.1)',
                    }}
                >
                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Typography 
                            variant="h4" 
                            sx={{
                                fontWeight: 800,
                                mb: 1,
                                background: isDark
                                    ? 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
                                    : 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                fontSize: { xs: '1.8rem', sm: '2.2rem' }
                            }}
                        >
                            Girvi Dashboard
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: 'text.secondary',
                                fontSize: '1.1rem'
                            }}
                        >
                            Overview of girvi principal, interest, received and pending amounts
                        </Typography>
                    </Box>

                    {/* Stats Cards */}
                    <Grid container spacing={3}>
                        {/* Total Principal */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{
                                backgroundColor: isDark 
                                    ? 'rgba(30, 41, 59, 0.5)'
                                    : 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${isDark 
                                    ? 'rgba(148, 163, 184, 0.1)'
                                    : 'rgba(255, 255, 255, 0.2)'
                                }`,
                                borderRadius: '16px',
                                boxShadow: isDark 
                                    ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                                    : '0 4px 20px rgba(31, 38, 135, 0.08)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: isDark 
                                        ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                                        : '0 8px 30px rgba(31, 38, 135, 0.12)',
                                }
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        gutterBottom
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Total Principal
                                    </Typography>
                                    <Typography 
                                        variant="h5" 
                                        fontWeight="bold"
                                        sx={{
                                            background: isDark
                                                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontSize: '1.8rem'
                                        }}
                                    >
                                        {formatCurrency(stats?.totalPrincipal)}
                                    </Typography>
                                    {/* <Typography variant="caption" sx={{ 
                                        color: 'text.secondary',
                                        mt: 1,
                                        display: 'block'
                                    }}>
                                        {filteredGirvi.length} records
                                    </Typography> */}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Total Interest Earned */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{
                                backgroundColor: isDark 
                                    ? 'rgba(30, 41, 59, 0.5)'
                                    : 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${isDark 
                                    ? 'rgba(148, 163, 184, 0.1)'
                                    : 'rgba(255, 255, 255, 0.2)'
                                }`,
                                borderRadius: '16px',
                                boxShadow: isDark 
                                    ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                                    : '0 4px 20px rgba(31, 38, 135, 0.08)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: isDark 
                                        ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                                        : '0 8px 30px rgba(31, 38, 135, 0.12)',
                                }
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        gutterBottom
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Total Interest Earned
                                    </Typography>
                                    <Typography 
                                        variant="h5" 
                                        fontWeight="bold"
                                        sx={{
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontSize: '1.8rem'
                                        }}
                                    >
                                        {formatCurrency(stats?.totalInterest)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Total Received */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{
                                backgroundColor: isDark 
                                    ? 'rgba(30, 41, 59, 0.5)'
                                    : 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${isDark 
                                    ? 'rgba(148, 163, 184, 0.1)'
                                    : 'rgba(255, 255, 255, 0.2)'
                                }`,
                                borderRadius: '16px',
                                boxShadow: isDark 
                                    ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                                    : '0 4px 20px rgba(31, 38, 135, 0.08)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: isDark 
                                        ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                                        : '0 8px 30px rgba(31, 38, 135, 0.12)',
                                }
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        gutterBottom
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Total Received
                                    </Typography>
                                    <Typography 
                                        variant="h5" 
                                        fontWeight="bold"
                                        sx={{
                                            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontSize: '1.8rem'
                                        }}
                                    >
                                        {formatCurrency(stats?.totalReceived)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Total Pending */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{
                                backgroundColor: isDark 
                                    ? 'rgba(30, 41, 59, 0.5)'
                                    : 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${isDark 
                                    ? 'rgba(148, 163, 184, 0.1)'
                                    : 'rgba(255, 255, 255, 0.2)'
                                }`,
                                borderRadius: '16px',
                                boxShadow: isDark 
                                    ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                                    : '0 4px 20px rgba(31, 38, 135, 0.08)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: isDark 
                                        ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                                        : '0 8px 30px rgba(31, 38, 135, 0.12)',
                                }
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        gutterBottom
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Total Pending
                                    </Typography>
                                    <Typography 
                                        variant="h5" 
                                        fontWeight="bold"
                                        sx={{
                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontSize: '1.8rem'
                                        }}
                                    >
                                        {formatCurrency(stats?.totalPending)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Add New Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/add-security-deposit')}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '1rem',
                                background: isDark
                                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                    : 'linear-gradient(135-gradient(135deg, #10b981 0%, #059669 100%)',
                                boxShadow: isDark
                                    ? '0 4px 14px 0 rgba(59, 130, 246, 0.35)'
                                    : '0 4px 14px 0 rgba(16, 185, 129, 0.35)',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    background: isDark
                                        ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                                        : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                    boxShadow: isDark
                                        ? '0 6px 20px 0 rgba(59, 130, 246, 0.45)'
                                        : '0 6px 20px 0 rgba(16, 185, 129, 0.45)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            Add New Security Deposit
                        </Button>
                    </Box>
                </Paper>

                {/* Girvi Records Table - Enhanced */}
                <Paper 
                    elevation={0}
                    sx={{
                        p: { xs: 3, sm: 4 },
                        backgroundColor: isDark 
                            ? 'rgba(30, 41, 59, 0.4)'
                            : 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: `1px solid ${isDark 
                            ? 'rgba(148, 163, 184, 0.1)'
                            : 'rgba(255, 255, 255, 0.2)'
                        }`,
                        borderRadius: '24px',
                        boxShadow: isDark 
                            ? '0 8px 32px rgba(0, 0, 0, 0.2)'
                            : '0 8px 32px rgba(31, 38, 135, 0.1)',
                        overflow: 'hidden',
                    }}
                >
                    {/* Active Filters */}
                    {activeFilters.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <FilterListIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Active Filters:
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={clearFilters}
                                    sx={{
                                        ml: 'auto',
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        color: isDark ? '#ef4444' : '#ef4444',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.error.main, 0.1)
                                        }
                                    }}
                                >
                                    Clear All
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {activeFilters.map((filter, index) => (
                                    <Chip
                                        key={index}
                                        label={filter}
                                        size="small"
                                        onDelete={() => removeFilter(index)}
                                        deleteIcon={<ClearIcon fontSize="small" />}
                                        sx={{
                                            backgroundColor: isDark 
                                                ? alpha('#3b82f6', 0.15)
                                                : alpha('#10b981', 0.15),
                                            backdropFilter: 'blur(10px)',
                                            border: `1px solid ${isDark 
                                                ? alpha('#3b82f6', 0.2)
                                                : alpha('#10b981', 0.2)
                                            }`,
                                            color: isDark ? '#60a5fa' : '#10b981',
                                            '& .MuiChip-deleteIcon': {
                                                color: isDark ? '#60a5fa' : '#10b981',
                                                '&:hover': {
                                                    color: isDark ? '#ef4444' : '#ef4444'
                                                }
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Table Header with Filters */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        <Box>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    fontSize: '1.3rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5
                                }}
                            >
                                Girvi Records
                                <Box component="span" sx={{ 
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '12px',
                                    backgroundColor: isDark 
                                        ? alpha('#3b82f6', 0.15)
                                        : alpha('#10b981', 0.15),
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${isDark 
                                        ? alpha('#3b82f6', 0.2)
                                        : alpha('#10b981', 0.2)
                                    }`,
                                    color: isDark ? '#60a5fa' : '#10b981'
                                }}>
                                    {filteredGirvi.length} of {girvi?.length || 0} Records
                                </Box>
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: 'text.secondary',
                                    mt: 0.5
                                }}
                            >
                                Manage all security deposit records
                            </Typography>
                        </Box>
                        
                        {/* Table Controls */}
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            {/* Search Input */}
                            <TextField
                                size="small"
                                placeholder="Search customer..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ 
                                                color: isDark ? '#94a3b8' : '#718096' 
                                            }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchTerm('')}
                                                sx={{ 
                                                    p: 0.5,
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.error.main, 0.1)
                                                    }
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '8px',
                                        backgroundColor: isDark 
                                            ? 'rgba(30, 41, 59, 0.5)'
                                            : 'rgba(255, 255, 255, 0.7)',
                                        backdropFilter: 'blur(10px)',
                                        border: `1px solid ${isDark 
                                            ? 'rgba(51, 65, 85, 0.6)'
                                            : 'rgba(226, 232, 240, 0.6)'
                                        }`,
                                        '&:hover': {
                                            borderColor: isDark ? '#3b82f6' : '#10b981',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: isDark ? '#3b82f6' : '#10b981',
                                        }
                                    }
                                }}
                                sx={{
                                    minWidth: '200px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            />

                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FilterListIcon />}
                                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    borderRadius: '8px',
                                    borderColor: isDark 
                                        ? 'rgba(148, 163, 184, 0.2)' 
                                        : 'rgba(226, 232, 240, 0.8)',
                                    backgroundColor: isDark 
                                        ? 'rgba(30, 41, 59, 0.3)' 
                                        : 'rgba(255, 255, 255, 0.3)',
                                    backdropFilter: 'blur(10px)',
                                    color: 'text.primary',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    '&:hover': {
                                        borderColor: isDark ? '#3b82f6' : '#10b981',
                                        backgroundColor: isDark 
                                            ? alpha('#3b82f6', 0.1) 
                                            : alpha('#10b981', 0.1),
                                    }
                                }}
                            >
                                Filters
                            </Button>
                        </Box>
                    </Box>

                    {/* Advanced Filters Collapse */}
                    <Collapse in={showFilters}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Paper 
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    backgroundColor: isDark 
                                        ? 'rgba(30, 41, 59, 0.3)'
                                        : 'rgba(255, 255, 255, 0.3)',
                                    backdropFilter: 'blur(20px)',
                                    border: `1px solid ${isDark 
                                        ? 'rgba(148, 163, 184, 0.1)'
                                        : 'rgba(255, 255, 255, 0.2)'
                                    }`,
                                    borderRadius: '16px',
                                }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Status"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: isDark 
                                                        ? 'rgba(30, 41, 59, 0.5)'
                                                        : 'rgba(255, 255, 255, 0.7)',
                                                    backdropFilter: 'blur(10px)',
                                                }
                                            }}
                                        >
                                            <MenuItem value="all">All Status</MenuItem>
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="completed">Completed</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Min Amount"
                                            value={amountRange.min}
                                            onChange={(e) => setAmountRange(prev => ({ ...prev, min: e.target.value }))}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoneyIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: isDark 
                                                        ? 'rgba(30, 41, 59, 0.5)'
                                                        : 'rgba(255, 255, 255, 0.7)',
                                                    backdropFilter: 'blur(10px)',
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Max Amount"
                                            value={amountRange.max}
                                            onChange={(e) => setAmountRange(prev => ({ ...prev, max: e.target.value }))}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoneyIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: isDark 
                                                        ? 'rgba(30, 41, 59, 0.5)'
                                                        : 'rgba(255, 255, 255, 0.7)',
                                                    backdropFilter: 'blur(10px)',
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DateRangeIcon />}
                                                onClick={() => {
                                                    // Set date range to last 30 days
                                                    const end = new Date();
                                                    const start = new Date();
                                                    start.setDate(start.getDate() - 30);
                                                    setDateRange({ start, end });
                                                }}
                                                sx={{
                                                    height: '40px',
                                                    width: '250px',
                                                    borderRadius: '8px',
                                                    borderColor: isDark 
                                                        ? 'rgba(148, 163, 184, 0.2)' 
                                                        : 'rgba(226, 232, 240, 0.8)',
                                                    backgroundColor: isDark 
                                                        ? 'rgba(30, 41, 59, 0.3)' 
                                                        : 'rgba(255, 255, 255, 0.3)',
                                                    backdropFilter: 'blur(10px)',
                                                    color: 'text.primary',
                                                }}
                                            >
                                                Last 30 Days
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                startIcon={<ClearIcon />}
                                                onClick={() => setDateRange({ start: null, end: null })}
                                                sx={{
                                                    height: '40px',
                                                    borderRadius: '8px',
                                                    borderColor: isDark 
                                                        ? 'rgba(148, 163, 184, 0.2)' 
                                                        : 'rgba(226, 232, 240, 0.8)',
                                                    backgroundColor: isDark 
                                                        ? 'rgba(30, 41, 59, 0.3)' 
                                                        : 'rgba(255, 255, 255, 0.3)',
                                                    backdropFilter: 'blur(10px)',
                                                    color: 'text.primary',
                                                }}
                                            >
                                                Clear Date
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </LocalizationProvider>
                    </Collapse>

                    {/* Rest of the table component remains the same */}
                    {loading ? (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            justifyContent: 'center', 
                            alignItems: 'center',
                            p: 8,
                            backgroundColor: isDark 
                                ? 'rgba(30, 41, 59, 0.3)'
                                : 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '16px',
                            backdropFilter: 'blur(10px)',
                            border: `1px dashed ${isDark 
                                ? 'rgba(148, 163, 184, 0.2)'
                                : 'rgba(226, 232, 240, 0.6)'
                            }`
                        }}>
                            <CircularProgress 
                                size={60}
                                thickness={3}
                                sx={{ 
                                    color: isDark ? '#3b82f6' : '#10b981',
                                    mb: 3
                                }} 
                            />
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    mb: 1
                                }}
                            >
                                Loading Girvi Records...
                            </Typography>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'text.secondary',
                                    opacity: 0.7
                                }}
                            >
                                Please wait while we fetch the data
                            </Typography>
                        </Box>
                    ) : filteredGirvi?.length === 0 ? (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            justifyContent: 'center', 
                            alignItems: 'center',
                            p: 6,
                            backgroundColor: isDark 
                                ? 'rgba(30, 41, 59, 0.3)'
                                : 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '16px',
                            backdropFilter: 'blur(10px)',
                            border: `1px dashed ${isDark 
                                ? 'rgba(148, 163, 184, 0.2)'
                                : 'rgba(226, 232, 240, 0.6)'
                            }`,
                            textAlign: 'center'
                        }}>
                            <Box sx={{ 
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isDark 
                                    ? alpha('#3b82f6', 0.1)
                                    : alpha('#10b981', 0.1),
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${isDark 
                                    ? alpha('#3b82f6', 0.2)
                                    : alpha('#10b981', 0.2)
                                }`,
                                mb: 3
                            }}>
                                <ReceiptLongIcon sx={{ 
                                    fontSize: 36,
                                    color: isDark ? '#3b82f6' : '#10b981',
                                    opacity: 0.8
                                }} />
                            </Box>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    mb: 1
                                }}
                            >
                                {activeFilters.length > 0 ? 'No Matching Records Found' : 'No Girvi Records Found'}
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: 'text.secondary',
                                    mb: 3,
                                    maxWidth: 400
                                }}
                            >
                                {activeFilters.length > 0 
                                    ? 'Try adjusting your filters to see more results'
                                    : 'Start by adding your first security deposit record to track loans and payments'}
                            </Typography>
                            {activeFilters.length > 0 ? (
                                <Button
                                    variant="outlined"
                                    onClick={clearFilters}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: isDark 
                                            ? 'rgba(148, 163, 184, 0.2)' 
                                            : 'rgba(226, 232, 240, 0.8)',
                                        color: isDark ? '#3b82f6' : '#10b981',
                                        '&:hover': {
                                            borderColor: isDark ? '#3b82f6' : '#10b981',
                                            backgroundColor: isDark 
                                                ? alpha('#3b82f6', 0.1) 
                                                : alpha('#10b981', 0.1),
                                        }
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate('/add-security-deposit')}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        background: isDark
                                            ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                            : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                        boxShadow: isDark
                                    ? '0 4px 14px 0 rgba(59, 130, 246, 0.35)'
                                    : '0 4px 14px 0 rgba(16, 185, 129, 0.35)',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            background: isDark
                                                ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                                                : 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                                            boxShadow: isDark
                                        ? '0 6px 20px 0 rgba(59, 130, 246, 0.45)'
                                        : '0 6px 20px 0 rgba(16, 185, 129, 0.45)',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    Add First Girvi
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <TableContainer sx={{ 
                            borderRadius: '16px',
                            backgroundColor: isDark 
                                ? 'rgba(30, 41, 59, 0.3)'
                                : 'rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${isDark 
                                ? 'rgba(51, 65, 85, 0.3)'
                                : 'rgba(226, 232, 240, 0.4)'
                            }`,
                            overflow: 'hidden',
                        }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ 
                                        backgroundColor: isDark 
                                            ? 'rgba(59, 130, 246, 0.1)'
                                            : 'rgba(16, 185, 129, 0.1)',
                                        backdropFilter: 'blur(10px)',
                                        borderBottom: `2px solid ${isDark 
                                            ? alpha('#3b82f6', 0.3)
                                            : alpha('#10b981', 0.3)
                                        }`
                                    }}>
                                        <TableCell sx={{ 
                                            fontWeight: 700, 
                                            color: 'text.primary',
                                            py: 2.5,
                                            px: 3,
                                            fontSize: '0.95rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="inherit">Customer Name</Typography>
                                                <Box sx={{ 
                                                    fontSize: '0.7rem',
                                                    px: 1,
                                                    py: 0.3,
                                                    borderRadius: '4px',
                                                    backgroundColor: isDark 
                                                        ? alpha('#3b82f6', 0.2)
                                                        : alpha('#10b981', 0.2),
                                                    color: isDark ? '#60a5fa' : '#10b981'
                                                }}>
                                                    {searchTerm ? 'Filtered' : 'A-Z'}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontWeight: 700, 
                                            color: 'text.primary',
                                            py: 2.5,
                                            px: 3,
                                            fontSize: '0.95rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Principal Amount
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontWeight: 700, 
                                            color: 'text.primary',
                                            py: 2.5,
                                            px: 3,
                                            fontSize: '0.95rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            textAlign: 'center'
                                        }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredGirvi?.map((g, index) => (
                                        <TableRow 
                                            key={g.id}
                                            sx={{ 
                                                borderBottom: `1px solid ${isDark 
                                                    ? 'rgba(51, 65, 85, 0.2)'
                                                    : 'rgba(226, 232, 240, 0.3)'
                                                }`,
                                                transition: 'all 0.2s ease',
                                                backgroundColor: index % 2 === 0 
                                                    ? isDark 
                                                        ? 'rgba(30, 41, 59, 0.15)'
                                                        : 'rgba(255, 255, 255, 0.2)'
                                                    : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: isDark 
                                                        ? 'rgba(255, 255, 255, 0.1)'
                                                        : 'rgba(16, 185, 129, 0.08)',
                                                    transform: 'translateX(4px)',
                                                    boxShadow: isDark 
                                                        ? '0 4px 12px rgba(0, 0, 0, 0.1)'
                                                        : '0 4px 12px rgba(16, 185, 129, 0.1)',
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ 
                                                color: 'text.primary', 
                                                py: 2.5,
                                                px: 3,
                                                fontWeight: 600,
                                                fontSize: '0.95rem'
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Box sx={{ 
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: isDark 
                                                            ? alpha('#3b82f6', 0.1)
                                                            : alpha('#10b981', 0.1),
                                                        color: isDark ? '#3b82f6' : '#10b981',
                                                        fontWeight: 700,
                                                        fontSize: '1.1rem'
                                                    }}>
                                                        {g.customerName.charAt(0).toUpperCase()}
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                            {g.customerName}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ 
                                                            color: 'text.secondary',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5
                                                        }}>
                                                            <Box component="span" sx={{ 
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: '50%',
                                                                backgroundColor: isDark ? '#3b82f6' : '#10b981'
                                                            }} />
                                                            Active
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                py: 2.5,
                                                px: 3
                                            }}>
                                                <Box>
                                                    <Typography sx={{ 
                                                        color: isDark ? '#3b82f6' : '#10b981', 
                                                        fontWeight: 700,
                                                        fontSize: '1.1rem'
                                                    }}>
                                                        {formatCurrency(parseFloat(g.principal))}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ 
                                                        color: 'text.secondary',
                                                        display: 'block',
                                                        mt: 0.5
                                                    }}>
                                                        Principal
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 2.5, px: 3 }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    gap: 1.5,
                                                    justifyContent: 'center'
                                                }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<PaymentIcon />}
                                                        onClick={() => navigate(`/security-deposits-payment/${g.id}`)}
                                                        sx={{
                                                            borderRadius: '8px',
                                                            borderColor: isDark 
                                                                ? alpha('#3b82f6', 0.3) 
                                                                : alpha('#10b981', 0.3),
                                                            backgroundColor: isDark 
                                                                ? alpha('#3b82f6', 0.1) 
                                                                : alpha('#10b981', 0.1),
                                                            backdropFilter: 'blur(10px)',
                                                            color: isDark ? '#3b82f6' : '#10b981',
                                                            fontSize: '0.85rem',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            px: 2,
                                                            py: 0.8,
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                borderColor: isDark ? '#3b82f6' : '#10b981',
                                                                backgroundColor: isDark 
                                                                    ? alpha('#3b82f6', 0.2) 
                                                                    : alpha('#10b981', 0.2),
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: isDark 
                                                                    ? '0 4px 12px rgba(59, 130, 246, 0.2)'
                                                                    : '0 4px 12px rgba(16, 185, 129, 0.2)',
                                                            }
                                                        }}
                                                    >
                                                        Payment
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<ReceiptLongIcon />}
                                                        onClick={() => navigate(`/security-deposit-ledger/${g.id}`)}
                                                        sx={{
                                                            borderRadius: '8px',
                                                            borderColor: isDark 
                                                                ? alpha('#8b5cf6', 0.3) 
                                                                : alpha('#8b5cf6', 0.3),
                                                            backgroundColor: isDark 
                                                                ? alpha('#8b5cf6', 0.1) 
                                                                : alpha('#8b5cf6', 0.1),
                                                            backdropFilter: 'blur(10px)',
                                                            color: isDark ? '#8b5cf6' : '#8b5cf6',
                                                            fontSize: '0.85rem',
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            px: 2,
                                                            py: 0.8,
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                borderColor: '#8b5cf6',
                                                                backgroundColor: isDark 
                                                                    ? alpha('#8b5cf6', 0.2) 
                                                                    : alpha('#8b5cf6', 0.2),
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: isDark 
                                                                    ? '0 4px 12px rgba(139, 92, 246, 0.2)'
                                                                    : '0 4px 12px rgba(139, 92, 246, 0.2)',
                                                            }
                                                        }}
                                                    >
                                                        Ledger
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            
                            {/* Table Footer */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                p: 2.5,
                                borderTop: `1px solid ${isDark 
                                    ? 'rgba(51, 65, 85, 0.2)'
                                    : 'rgba(226, 232, 240, 0.3)'
                                }`,
                                backgroundColor: isDark 
                                    ? 'rgba(30, 41, 59, 0.2)'
                                    : 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                            }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Showing {filteredGirvi.length} of {girvi?.length || 0} records
                                    {activeFilters.length > 0 && ' (filtered)'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        disabled
                                        sx={{
                                            borderRadius: '6px',
                                            fontSize: '0.8rem',
                                            fontWeight: 500,
                                            color: 'text.secondary',
                                            minWidth: '32px',
                                            height: '32px'
                                        }}
                                    >
                                        ←
                                    </Button>
                                    <Button
                                        size="small"
                                        sx={{
                                            borderRadius: '6px',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            backgroundColor: isDark 
                                                ? alpha('#3b82f6', 0.1)
                                                : alpha('#10b981', 0.1),
                                            color: isDark ? '#3b82f6' : '#10b981',
                                            minWidth: '32px',
                                            height: '32px'
                                        }}
                                    >
                                        1
                                    </Button>
                                    <Button
                                        size="small"
                                        disabled
                                        sx={{
                                            borderRadius: '6px',
                                            fontSize: '0.8rem',
                                            fontWeight: 500,
                                            color: 'text.secondary',
                                            minWidth: '32px',
                                            height: '32px'
                                        }}
                                    >
                                        →
                                    </Button>
                                </Box>
                            </Box>
                        </TableContainer>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default ViewGirvi;